import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { DateTime } from 'luxon'
import { ChatStatus, MessagesState, RootState } from 'types/state'
import _ from 'lodash'
import {
  AgentActionStatusEnum,
  ChatMessage,
  FeedbackEnum,
  MessageSourceEnum,
  MessageTypeEnum,
} from 'types/chat-message'
import { ChatAttachment } from 'types/chat-attachment'
import { Chat, ChatResponseModeEnum, ChatRoleEnum } from 'types/chat'
import { findParticipantsByRole } from 'utils/chats'
import { TicketStatusEnum } from 'types/ticket'
import { TicketStatusUpdateBody } from 'types/index'

const initialState: MessagesState = {
  openChatLoading: false,
  chatId: undefined,
  chats: {},
  messagesByChat: {},
  statusByChat: {},
  chatInitialized: false,
}

const statusOf = (state: MessagesState, chatId: string | undefined): ChatStatus =>
  (chatId && state.statusByChat[chatId]) || 'idle'

export const messagesSlice = createSlice({
  name: 'messages',
  initialState,
  reducers: {
    setChatId: (state, action: PayloadAction<MessagesState['chatId']>) => {
      state.chatId = action.payload
    },
    setChatInitialized: (state, action: PayloadAction<MessagesState['chatInitialized']>) => {
      state.chatInitialized = action.payload
    },
    setOpenChatLoading: (state, action: PayloadAction<MessagesState['openChatLoading']>) => {
      state.openChatLoading = action.payload
    },
    resetMessageState: () => initialState,
    addChat: (state, { payload: newChat }: PayloadAction<Chat>) => {
      state.chats = { [newChat._id]: newChat, ...state.chats }
    },
    addChats: (state, { payload: newChats }: PayloadAction<Chat[]>) => {
      state.chats = {}
      newChats.forEach((chat) => {
        state.chats[chat._id] = chat
      })
    },
    setChats: (state, action: PayloadAction<MessagesState['chats']>) => {
      state.chats = action.payload
    },
    setChat: (state, { payload: chat }: PayloadAction<Chat>) => {
      state.chats[chat._id] = chat
    },
    setTicketStatus: (state, { payload }: PayloadAction<TicketStatusUpdateBody>) => {
      const ticket = state.chats[payload.chatId]?.ticket
      if (ticket && ticket._id === payload.ticketId) {
        ticket.status = payload.status
      }
    },
    editChat: (state, { payload: chat }: PayloadAction<Chat>) => {
      state.chats[chat._id] = chat
    },
    updateChat: (state, { payload }: PayloadAction<Partial<Chat>>) => {
      if (payload._id && state.chats[payload._id]) {
        state.chats[payload._id].lastMessage = payload.lastMessage
      }
    },
    updateCurrentChat: (state, action: PayloadAction<Partial<Chat>>) => {
      if (state.chatId && state.chats[state.chatId]) {
        state.chats[state.chatId] = { ...state.chats[state.chatId], ...action.payload }
      }
    },
    removeChat: (state, { payload: chatId }: PayloadAction<string>) => {
      delete state.chats[chatId]
      delete state.messagesByChat[chatId]
      delete state.statusByChat[chatId]
    },
    updateChatLastMessage: (state) => {
      const chatId = state.chatId
      const lastMessage = chatId ? _.last(state.messagesByChat[chatId]) : undefined
      if (chatId && lastMessage && state.chats[chatId]) {
        state.chats[chatId].lastMessage = lastMessage
        state.chats[chatId].updatedAt = String(DateTime.now().toISO())
      }
    },
    setChatMessages: (state, { payload }: PayloadAction<{ chatId: string; messages: ChatMessage[] }>) => {
      const serverIds = new Set(payload.messages.map((message) => message._id))
      const localOnly = (state.messagesByChat[payload.chatId] ?? []).filter((message) => !serverIds.has(message._id))
      state.messagesByChat[payload.chatId] = [...payload.messages, ...localOnly]
    },
    addUserMessage: (
      state,
      action: PayloadAction<{
        chatId: string
        _id: string
        message: string
        senderId: string
        chatSenderRole: ChatRoleEnum
        attachments?: ChatAttachment[]
      }>,
    ) => {
      const { chatId, _id, message, senderId, chatSenderRole, attachments } = action.payload
      if (!state.messagesByChat[chatId]) {
        state.messagesByChat[chatId] = []
      }
      state.messagesByChat[chatId].push({
        _id,
        message,
        senderId,
        chatSenderRole,
        chatId,
        attachments,
        attachmentIds: attachments?.map((attachment) => attachment._id),
        type: MessageTypeEnum.TEXT,
        chatResponseMode: ChatResponseModeEnum.BOT,
        createdAt: String(DateTime.now().toISO()),
        timestamp: DateTime.now().toMillis(),
      })
    },
    upsertBotMessage: (
      state,
      action: PayloadAction<{ chatId: string; botMessageId: string; partialResponse: string; senderId: string }>,
    ) => {
      const { chatId, botMessageId, partialResponse, senderId } = action.payload
      if (!state.messagesByChat[chatId]) {
        state.messagesByChat[chatId] = []
      }
      const list = state.messagesByChat[chatId]
      const existing = list.find((message) => message._id === botMessageId)
      if (existing) {
        existing.message = partialResponse
      } else {
        list.push({
          _id: botMessageId,
          message: partialResponse,
          senderId,
          chatSenderRole: ChatRoleEnum.BOT,
          chatId,
          type: MessageTypeEnum.TEXT,
          chatResponseMode: ChatResponseModeEnum.BOT,
          messageSource: MessageSourceEnum.localBotResponse,
          createdAt: String(DateTime.now().toISO()),
          timestamp: DateTime.now().toMillis(),
        })
      }
    },
    addIncomingMessage: (state, { payload: message }: PayloadAction<ChatMessage>) => {
      if (!message.chatId) {
        return
      }
      if (!state.messagesByChat[message.chatId]) {
        state.messagesByChat[message.chatId] = []
      }
      const list = state.messagesByChat[message.chatId]
      if (!list.some((item) => item._id === message._id)) {
        list.push(message)
      }
    },
    setMessageFeedback: (
      state,
      { payload }: PayloadAction<{ chatId: string; messageId: string; feedback: FeedbackEnum }>,
    ) => {
      const message = state.messagesByChat[payload.chatId]?.find((item) => item._id === payload.messageId)
      if (message) {
        message.feedback = payload.feedback
      }
    },
    setChatStatus: (state, { payload }: PayloadAction<{ chatId: string; status: ChatStatus }>) => {
      if (payload.status === 'idle') {
        delete state.statusByChat[payload.chatId]
      } else {
        state.statusByChat[payload.chatId] = payload.status
      }
    },
    setAgentActionStatus: (state, { payload }: PayloadAction<{ runId: string; status: AgentActionStatusEnum }>) => {
      for (const messages of Object.values(state.messagesByChat)) {
        const message = messages.find((m) => m.agentRunId === payload.runId)
        if (message) {
          message.agentActionStatus = payload.status
          break
        }
      }
    },
  },
})

export const {
  setChatId,
  setChatInitialized,
  setOpenChatLoading,
  resetMessageState,
  addChat,
  addChats,
  setChats,
  setChat,
  editChat,
  updateChat,
  updateCurrentChat,
  removeChat,
  updateChatLastMessage,
  setChatMessages,
  addUserMessage,
  upsertBotMessage,
  addIncomingMessage,
  setMessageFeedback,
  setChatStatus,
  setAgentActionStatus,
} = messagesSlice.actions

export const messagesActions = messagesSlice.actions

const EMPTY_MESSAGES: ChatMessage[] = []

export const selectChatId = (state: RootState) => state.messageState.chatId
export const selectOpenChatLoading = (state: RootState) => state.messageState.openChatLoading
export const selectChatInitialized = (state: RootState) => state.messageState.chatInitialized
export const selectMessages = (state: RootState) =>
  (state.messageState.chatId && state.messageState.messagesByChat?.[state.messageState.chatId]) || EMPTY_MESSAGES
export const selectHasMessages = (state: RootState) => selectMessages(state).length > 0

const selectCurrentStatus = (state: RootState): ChatStatus => statusOf(state.messageState, state.messageState.chatId)

const selectLastBotModeMessageId = (state: RootState) => {
  const chatId = selectChatId(state)
  const ticket = chatId ? state.messageState.chats[chatId]?.ticket : undefined
  if (!ticket) {
    return undefined
  }
  const lastBotMessage = [...selectMessages(state)]
    .reverse()
    .find((m) => m.chatResponseMode === ChatResponseModeEnum.BOT && m.chatSenderRole === ChatRoleEnum.BOT)
  return lastBotMessage?._id
}

export const messagesSelectors = {
  chatId: selectChatId,
  chats: (state: RootState) => state.messageState.chats,
  lastBotModeMessageId: selectLastBotModeMessageId,
  guestChats: (state: RootState) => {
    const isLogged = !!state.settingsState.token
    const botChats = _.values(state.messageState.chats).filter((chat) => chat.responseMode === ChatResponseModeEnum.BOT)
    return botChats.filter((chat) => {
      const guest = findParticipantsByRole(chat, isLogged ? ChatRoleEnum.USER : ChatRoleEnum.GUEST)
      return guest?.userId === chat.userId
    })
  },
  currentChat: (state: RootState) => {
    const chatId = selectChatId(state)
    return chatId ? state.messageState.chats[chatId] : undefined
  },
  currentChatBotId: (state: RootState) => {
    const chatId = selectChatId(state)
    return chatId ? findParticipantsByRole(state.messageState.chats[chatId], ChatRoleEnum.BOT)?.userId : undefined
  },
  currentChatTicket: (state: RootState) => {
    const chatId = selectChatId(state)
    return chatId ? state.messageState.chats[chatId]?.ticket : undefined
  },
  currentChatIsInOperatorMode: (state: RootState) => {
    const chatId = selectChatId(state)
    return chatId ? state.messageState.chats[chatId]?.responseMode === ChatResponseModeEnum.OPERATOR : false
  },
  currentChatParticipants: (state: RootState) => {
    const chatId = selectChatId(state)
    return (chatId ? state.messageState.chats[chatId]?.participants : undefined) || []
  },
  disableFieldsTicketClosed: (state: RootState) => {
    const ticket = messagesSelectors.currentChatTicket(state)
    if (!ticket) {
      return false
    }
    return !(ticket.status === TicketStatusEnum.NEW || ticket.status === TicketStatusEnum.IN_PROGRESS)
  },
  // UI affordances derived from the open chat's status — a single source of truth.
  userTyping: (state: RootState) => {
    const status = selectCurrentStatus(state)
    return status === 'idle' || status === 'sending'
  },
  userTypingPlaceholder: (state: RootState) => {
    const status = selectCurrentStatus(state)
    return status === 'awaitingBot' || status === 'streaming' ? 'waitBotResponse' : 'typeMessage'
  },
  botTyping: (state: RootState) => selectCurrentStatus(state) === 'awaitingBot',
  currentChatSending: (state: RootState) => selectCurrentStatus(state) !== 'idle',
  // Server-persisted action status for a run (survives reload).
  agentActionStatusByRun: (runId: string) => (state: RootState) => {
    const chatId = state.messageState.chatId
    if (!chatId) return undefined
    return state.messageState.messagesByChat[chatId]?.find((m) => m.agentRunId === runId)?.agentActionStatus
  },
  selectOpenChatLoading,
  selectMessages,
  selectHasMessages,
  selectChatInitialized,
}

export default messagesSlice.reducer
